import { plPL } from "@clerk/localizations";
import { ClerkProvider } from "@clerk/react-router";
import { clerkMiddleware, rootAuthLoader } from "@clerk/react-router/server";
import * as Sentry from "@sentry/react";
import { posthog } from "posthog-js";
import { useEffect } from "react";
import * as ReactRouter from "react-router";
import { CrispChat } from "./components/crisp-chat";
import { PosthogUser } from "./components/posthog";
import { SentryUser } from "./components/sentry-user";
import { Toaster } from "./components/ui/toaster";
import * as Paths from "./paths";
import stylesheet from "./tailwind.css?url";
import { Route } from "../.react-router/types/app/+types/root";

export const meta: ReactRouter.MetaFunction = () => [{ title: "AnyLawyer" }];

export const links: Route.LinksFunction = () => [
  { rel: "stylesheet", href: stylesheet },
  { rel: "preconnect", href: "https://fonts.googleapis.com" },
  {
    rel: "preconnect",
    href: "https://fonts.gstatic.com",
    crossOrigin: "anonymous",
  },
  {
    rel: "stylesheet",
    href: "https://fonts.googleapis.com/css2?family=Inter:wght@100..900&family=Playfair+Display:ital,wght@0,400..900;1,400..900&display=swap",
  },
];

export const middleware: Route.MiddlewareFunction[] = [clerkMiddleware()];

export async function loader(args: Route.LoaderArgs) {
  return rootAuthLoader(args, async () => {
    return {
      config: {
        PARROT_ENV: process.env.PARROT_ENV,
        ACCOUNT_PORTAL_URL: process.env.ACCOUNT_PORTAL_URL,
      },
    };
  });
}

export default function App({ loaderData }: Route.ComponentProps) {
  const location = ReactRouter.useLocation();
  useEffect(() => {
    posthog.capture("$pageview");
  }, [location]);

  let locale = "pl";
  if (location.pathname === "/") {
    locale = "en";
  }

  return (
    <html lang={locale}>
      <head>
        <ReactRouter.Meta />
        <ReactRouter.Links />
        <meta charSet="utf-8" />
        <meta name="viewport" content="width=device-width,initial-scale=1" />
      </head>
      <body>
        <ClerkProvider
          loaderData={loaderData}
          signInUrl={Paths.login()}
          signUpUrl={Paths.index()}
          signUpFallbackRedirectUrl={Paths.app()}
          signInFallbackRedirectUrl={Paths.app()}
          localization={plPL}
        >
          <SentryUser />
          <PosthogUser />
          <CrispChat />
          <ReactRouter.Outlet />
          <Toaster />
          <ReactRouter.ScrollRestoration />
          <ReactRouter.Scripts />
          <script
            dangerouslySetInnerHTML={{
              __html: `window.PARROT_CONFIG = ${JSON.stringify(loaderData?.config ?? {})};`,
            }}
          />
        </ClerkProvider>
      </body>
    </html>
  );
}

export function ErrorBoundary({ error }: Route.ErrorBoundaryProps) {
  let message = "Oops!";
  let details = "An unexpected error occurred.";
  let stack: string | undefined;

  if (ReactRouter.isRouteErrorResponse(error)) {
    message = error.status === 404 ? "404" : "Error";
    details =
      error.status === 404
        ? "The requested page could not be found."
        : error.statusText || details;
  } else if (error && error instanceof Error) {
    // you only want to capture non 404-errors that reach the boundary
    Sentry.captureException(error);
    if (import.meta.env.DEV) {
      details = error.message;
      stack = error.stack;
    }
  }

  return (
    <main>
      <h1>{message}</h1>
      <p>{details}</p>
      {stack && (
        <pre>
          <code>{stack}</code>
        </pre>
      )}
    </main>
  );
}
